import { Controller, FieldError, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { HireWorkDaysFormValues } from "./types"
import { TimeInput, type TimeInputProps } from "./TimeInput"
import { WeekDaysEnum } from "@oysterhr/schema"
import { Text } from "@oysterhr/ui"

export interface HireWorkScheduleFormProps extends Pick<TimeInputProps, "isClearable"> {
  day: WeekDaysEnum
  errors?: {
    from?: FieldError
    to?: FieldError
    break?: FieldError
  }
  onWorkScheduleChange?: (workDays: HireWorkDaysFormValues["workDays"]) => void
  form: UseFormReturn<HireWorkDaysFormValues>
}

export function HireWorkScheduleForm({
  day,
  errors,
  isClearable = true,
  onWorkScheduleChange,
  form,
}: HireWorkScheduleFormProps) {
  const { t } = useTranslation("WorkScheduleForm")

  const { getValues, clearErrors, control } = form

  return (
    <div className="flex flex-col">
      <div className="flex min-h-9 items-center justify-between space-x-6">
        <Text className="min-w-[87px]">{t(`form.weekdays.long.${day}`)}</Text>
        <div className="flex items-center gap-3">
          <div className="h-8 w-24">
            <Controller
              control={control}
              name={`workDays.${day}.from`}
              rules={{
                validate: value => {
                  if (!value || (value && getValues(`workDays.${day}.to`) === null)) {
                    return t("validations.workScheduleRequired") as string
                  }
                  if (getValues(`workDays.${day}.to`) < value) {
                    return t("validations.fromGreaterThanTo") as string
                  }
                  return true
                },
              }}
              render={({ field: { value, onChange, name } }) => (
                <TimeInput
                  name={name}
                  isClearable={isClearable}
                  value={value}
                  onChange={value => {
                    onChange(value)
                    clearErrors("workDays")
                    onWorkScheduleChange?.(getValues("workDays"))
                  }}
                  error={errors?.from}
                />
              )}
            />
          </div>

          <Text>{t("form.to")}</Text>

          <div className="h-8 w-24">
            <Controller
              control={control}
              name={`workDays.${day}.to`}
              rules={{
                validate: value => {
                  if (!value || (value && getValues(`workDays.${day}.from`) === null)) {
                    return t("validations.workScheduleRequired") as string
                  }
                  if (getValues(`workDays.${day}.from`) > value) {
                    return t("validations.fromGreaterThanTo") as string
                  }
                  return true
                },
              }}
              render={({ field: { value, onChange, name } }) => (
                <TimeInput
                  name={name}
                  isClearable={isClearable}
                  value={value}
                  onChange={value => {
                    onChange(value)
                    clearErrors("workDays")
                    onWorkScheduleChange?.(getValues("workDays"))
                  }}
                  error={errors?.to}
                />
              )}
            />
          </div>
        </div>
      </div>

      <Text className="text-error-base">
        {errors && (errors.from?.message || errors.to?.message)}
      </Text>
    </div>
  )
}
